import { Eye, Link2, Plus, X } from "lucide-react";
import type {
  ProductRequest,
  ProductRequestStatus,
} from "@/features/productRequest/types";
import "./ProductLedgers.css";

export type ProductRequestsLedgerProps = {
  requests: ProductRequest[];
  variant: "super" | "supplier";
  onReview?: (
    request: ProductRequest,
    mode: "create" | "map" | "reject",
  ) => void;
  onView?: (request: ProductRequest) => void;
};

const STATUS_LABELS: Record<ProductRequestStatus, string> = {
  pending: "Pendente",
  approved_created: "Aprovado e criado",
  approved_mapped: "Vinculado",
  rejected: "Rejeitado",
};

const SAO_PAULO_TIME_ZONE = "America/Sao_Paulo";
const REQUEST_DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  timeZone: SAO_PAULO_TIME_ZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function formatDate(value?: string) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }

  return REQUEST_DATE_FORMATTER.format(date);
}

export function ProductRequestsLedger({
  requests,
  variant,
  onReview,
  onView,
}: ProductRequestsLedgerProps) {
  if (requests.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Solicitações de produtos"
      className="product-request-ledger"
      role="list"
    >
      {requests.map((request) => {
        const createdAt = formatDate(request.created_at);
        const reviewedAt = formatDate(request.reviewed_at);
        const canReview =
          variant === "super" &&
          request.status === "pending" &&
          onReview !== undefined;
        const canView = variant === "supplier" && onView !== undefined;

        return (
          <article
            className="product-request-ledger-row"
            data-has-actions={canReview || canView}
            data-testid={`product-request-row-${request.id}`}
            key={request.id}
            role="listitem"
          >
            <div className="product-request-content">
              <div className="product-request-heading">
                <h3 className="product-request-name">
                  {request.requested_name}
                </h3>
                <span
                  aria-label={`Status: ${STATUS_LABELS[request.status]}`}
                  className="product-request-status"
                  data-status={request.status}
                >
                  {STATUS_LABELS[request.status]}
                </span>
              </div>

              <div className="product-request-facts">
                <div className="product-request-fact-values">
                  {request.supplier_name ? (
                    <span className="product-request-supplier">
                      {request.supplier_name}
                    </span>
                  ) : null}
                  {createdAt ? (
                    <time dateTime={request.created_at}>{createdAt}</time>
                  ) : null}
                </div>
                {canView ? (
                  <div
                    aria-label={`Ações para ${request.requested_name}`}
                    className="product-request-view-actions"
                    role="group"
                  >
                    <button
                      aria-label={`Ver ${request.requested_name}`}
                      className="product-request-view-action"
                      onClick={() => onView(request)}
                      type="button"
                    >
                      <Eye aria-hidden="true" size={16} strokeWidth={1.8} />
                      Ver
                    </button>
                  </div>
                ) : null}
              </div>

              {request.note || request.decision_note || reviewedAt ? (
                <div className="product-request-notes">
                  {request.note ? (
                    <p>
                      <strong>Observação</strong>
                      {request.note}
                    </p>
                  ) : null}
                  {request.decision_note ? (
                    <p>
                      <strong>Retorno</strong>
                      {request.decision_note}
                    </p>
                  ) : null}
                  {reviewedAt ? (
                    <span className="product-request-reviewed-at">
                      Analisado em{" "}
                      <time dateTime={request.reviewed_at}>{reviewedAt}</time>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {canReview ? (
              <div
                aria-label={`Ações para ${request.requested_name}`}
                className="product-request-actions"
                role="group"
              >
                <button
                  aria-label={`Criar ${request.requested_name}`}
                  onClick={() => onReview(request, "create")}
                  type="button"
                >
                  <Plus aria-hidden="true" size={16} strokeWidth={1.9} />
                  Criar
                </button>
                <button
                  aria-label={`Vincular ${request.requested_name}`}
                  onClick={() => onReview(request, "map")}
                  type="button"
                >
                  <Link2 aria-hidden="true" size={15} strokeWidth={1.9} />
                  Vincular
                </button>
                <button
                  aria-label={`Rejeitar ${request.requested_name}`}
                  className="product-request-reject"
                  onClick={() => onReview(request, "reject")}
                  type="button"
                >
                  <X aria-hidden="true" size={16} strokeWidth={1.9} />
                  Rejeitar
                </button>
              </div>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}
